/**
 * StatusActions - Pipeline status management with move-to buttons.
 * Shows current status badge and available transitions.
 */

import { useState, useCallback } from 'react';
import type { PipelineJob, PipelineStatus } from '@/types/pipeline';
import { usePipelineStore } from '@/store/pipeline.store';

interface StatusActionsProps {
  job: PipelineJob;
}

interface StatusTransition {
  target: PipelineStatus;
  label: string;
  isDanger?: boolean;
}

/**
 * Get available status transitions from the current status.
 */
function getAvailableTransitions(current: PipelineStatus): StatusTransition[] {
  const transitions: StatusTransition[] = [];

  switch (current) {
    case 'saved':
      transitions.push({ target: 'applied', label: 'Mark as Applied' });
      transitions.push({ target: 'rejected', label: 'Mark as Rejected', isDanger: true });
      break;
    case 'applied':
      transitions.push({ target: 'interview', label: 'Mark as Interview' });
      transitions.push({ target: 'rejected', label: 'Mark as Rejected', isDanger: true });
      break;
    case 'interview':
      transitions.push({ target: 'offer', label: 'Mark as Offer' });
      transitions.push({ target: 'rejected', label: 'Mark as Rejected', isDanger: true });
      break;
    case 'offer':
      transitions.push({ target: 'rejected', label: 'Mark as Rejected', isDanger: true });
      break;
    case 'rejected':
      transitions.push({ target: 'saved', label: 'Move back to Saved' });
      break;
  }

  return transitions;
}

export function StatusActions({ job }: StatusActionsProps) {
  const moveJob = usePipelineStore((s) => s.moveJob);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleMove = useCallback(
    (target: PipelineStatus) => {
      moveJob(job.id, target);
      setSuccessMessage(`Moved to "${target}"`);

      // Clear success message after 2 seconds
      setTimeout(() => setSuccessMessage(null), 2000);
    },
    [job.id, moveJob]
  );

  const transitions = getAvailableTransitions(job.status);
  const badgeClass = `status-actions__badge status-actions__badge--${job.status}`;

  return (
    <div className="status-actions">
      <p className="status-actions__label">Pipeline Status</p>

      <div className="status-actions__current">
        <span className={badgeClass}>{job.status}</span>
      </div>

      {transitions.length > 0 && (
        <div className="status-actions__buttons">
          {transitions.map((transition) => (
            <button
              key={transition.target}
              className={`status-actions__btn ${transition.isDanger ? 'status-actions__btn--danger' : ''}`}
              onClick={() => handleMove(transition.target)}
            >
              <svg
                className="status-actions__btn-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {transition.isDanger ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                )}
              </svg>
              {transition.label}
            </button>
          ))}
        </div>
      )}

      {successMessage && (
        <p className="status-actions__success-msg">{successMessage}</p>
      )}
    </div>
  );
}
